import { CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button.js";
import type { GrammarExercise } from "../../data/grammar.js";

export function GrammarQuiz({ exercises }: { exercises: GrammarExercise[] }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<number, number>>({});

  const answeredCount = Object.keys(answers).length;
  const correctCount = exercises.reduce(
    (sum, exercise, idx) => sum + (answers[idx] === exercise.correctIndex ? 1 : 0),
    0,
  );
  const allAnswered = answeredCount === exercises.length;

  return (
    <div className="space-y-4">
      {exercises.map((exercise, idx) => {
        const picked = answers[idx];
        const isAnswered = picked !== undefined;
        return (
          <div key={idx} className="rounded-lg border border-[var(--glass-border)] p-4">
            <p className="text-sm font-medium text-[var(--text-primary)]">
              <span className="mr-1.5 text-[var(--text-muted)]">{idx + 1}.</span>
              {exercise.question}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {exercise.options.map((option, optionIdx) => {
                const isPicked = picked === optionIdx;
                const isCorrect = optionIdx === exercise.correctIndex;
                let style = "border-[var(--glass-border)] text-[var(--text-secondary)] hover:bg-white/5 hover:text-[var(--text-primary)]";
                if (isAnswered) {
                  if (isCorrect) {
                    style = "border-emerald-400/50 bg-emerald-400/10 text-emerald-200";
                  } else if (isPicked) {
                    style = "border-rose-400/50 bg-rose-400/10 text-rose-200";
                  } else {
                    style = "border-[var(--glass-border)] text-[var(--text-muted)]";
                  }
                }
                return (
                  <button
                    key={optionIdx}
                    type="button"
                    disabled={isAnswered}
                    onClick={() => setAnswers((prev) => ({ ...prev, [idx]: optionIdx }))}
                    className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default ${style}`}
                  >
                    <span>{option}</span>
                    {isAnswered && isCorrect ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                    ) : null}
                    {isAnswered && isPicked && !isCorrect ? (
                      <XCircle className="h-4 w-4 shrink-0" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
            {isAnswered ? (
              <p className="mt-3 rounded-lg bg-white/[0.03] p-3 text-xs leading-relaxed text-[var(--text-muted)]">
                {exercise.explanationVi}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[var(--text-secondary)]">
          {allAnswered
            ? t("grammarQuiz.finalScore", { correct: correctCount, total: exercises.length })
            : t("grammarQuiz.progress", { answered: answeredCount, total: exercises.length })}
        </p>
        {answeredCount > 0 ? (
          <Button variant="secondary" onClick={() => setAnswers({})}>
            {t("grammarQuiz.retry")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
